import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { bindBackButton, haptic } from '../lib/telegram.js';

/**
 * Marshrutlash — STEK ko'rinishida, URL'siz.
 *
 * Telegram Mini App da URL semantikasi yo'q: foydalanuvchi havolani
 * ulashmaydi va brauzer tarixi ko'rinmaydi. Kerak bo'lgani —
 * "ochish / orqaga qaytish" steki va uning Telegram BackButton bilan
 * bog'lanishi. Shuning uchun to'liq router kutubxonasi ishlatilmaydi:
 * u bundle byudjetiga (200 KB gzip) hech narsa qo'shmasdan yuk bo'lardi.
 */
export type Route =
  | { name: 'home' }
  | { name: 'search'; query?: string }
  | { name: 'catalog'; categoryId?: string; title?: string }
  | { name: 'categories' }
  | { name: 'product'; slug: string }
  | { name: 'cart' }
  | { name: 'checkout' }
  | { name: 'success'; orderId: string; number: string }
  | { name: 'orders' }
  | { name: 'order'; id: string }
  | { name: 'shipment'; id: string }
  | { name: 'garage' }
  | { name: 'profile' };

/** Tab bar'da ko'rinadigan ildiz ekranlar. */
export const ROOT_ROUTES = ['home', 'catalog', 'cart', 'orders', 'profile'] as const;
export type RootRouteName = (typeof ROOT_ROUTES)[number];

interface NavigationValue {
  route: Route;
  /** Stek chuqurligi — 1 bo'lsa ildizdamiz. */
  depth: number;
  push: (route: Route) => void;
  /** Joriy ekranni almashtiradi (stek o'smaydi, scroll joyida qoladi) — kategoriyadan kategoriyaga. */
  replace: (route: Route) => void;
  back: () => void;
  /** Ildiz ekranga o'tish — stek tozalanadi. */
  switchRoot: (name: RootRouteName) => void;
}

const NavigationContext = createContext<NavigationValue | null>(null);

/**
 * Ildiz marshrut — MODUL DARAJASIDA doimiy.
 *
 * Har renderda yangi obyekt yasalsa, `useMemo` bog'liqliklari doim
 * o'zgaradi va butun daraxt qayta chiziladi.
 */
const HOME: Route = { name: 'home' };

export function NavigationProvider({ children }: { children: ReactNode }): ReactNode {
  const [stack, setStack] = useState<Route[]>([HOME]);

  /*
   * Har ekranning scroll joyi eslab qolinadi: ro'yxatdan kategoriya/tovarga kirib, "orqaga"
   * bosganda mijoz o'sha joyga qaytadi, tepaga sakramaydi. Ilgari oldingi ekran o'chirib
   * tashlanardi va u qayta chizilganda scroll 0 bo'lib qolardi.
   */
  const scrolls = useRef<number[]>([]);
  const restore = useRef<number | null>(null);

  const push = useCallback((route: Route) => {
    haptic('light');
    scrolls.current.push(globalThis.window.scrollY);
    setStack((current) => [...current, route]);
    // Yangi ekran tepadan boshlanadi.
    globalThis.window.scrollTo(0, 0);
  }, []);

  const replace = useCallback((route: Route) => {
    haptic('light');
    setStack((current) => [...current.slice(0, -1), route]);
  }, []);

  const back = useCallback(() => {
    setStack((current) => {
      if (current.length <= 1) return current;
      restore.current = scrolls.current.pop() ?? 0;
      return current.slice(0, -1);
    });
  }, []);

  useLayoutEffect(() => {
    const target = restore.current;
    if (target === null) return;
    restore.current = null;
    globalThis.window.scrollTo(0, target);
    // Ro'yxat keshdan darhol chizilmasa (balandlik hali yetmasa) bir kadrdan keyin yana urinamiz.
    globalThis.requestAnimationFrame(() => {
      globalThis.window.scrollTo(0, target);
    });
  }, [stack]);

  const switchRoot = useCallback((name: RootRouteName) => {
    haptic('light');
    // Ildizga o'tishda stek tozalanadi — aks holda "orqaga" tugmasi
    // foydalanuvchini kutilmagan ekranga olib boradi.
    scrolls.current = [];
    setStack([{ name } as Route]);
    globalThis.window.scrollTo(0, 0);
  }, []);

  const route = stack[stack.length - 1] ?? HOME;
  const depth = stack.length;

  // Telegram'ning O'Z orqaga tugmasi ishlatiladi — ilovada alohida
  // tugma chizilmaydi (TWA odati).
  useEffect(() => bindBackButton(depth > 1 ? back : null), [depth, back]);

  const value = useMemo<NavigationValue>(
    () => ({ route, depth, push, replace, back, switchRoot }),
    [route, depth, push, replace, back, switchRoot],
  );

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
}

export function useNavigation(): NavigationValue {
  const value = useContext(NavigationContext);
  if (value === null) throw new Error('useNavigation faqat NavigationProvider ichida ishlaydi');
  return value;
}
